import React, { useEffect, useState } from "react";

const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return "Expired";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}h ${m}m ${s < 10 ? "0" : ""}${s}s`;
  }
  return `${m}m ${s < 10 ? "0" : ""}${s}s`;
};

const ActiveChallengeBanner = ({
  challenge,
  user,
  group,
  onAcceptChallenge,
  onViewLeaderboard,
  onCancelChallenge,
  onOpenHistory,
}) => {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!challenge?.expiresAt) return 0;
    return Math.max(
      0,
      Math.floor((new Date(challenge.expiresAt).getTime() - Date.now()) / 1000)
    );
  });

  useEffect(() => {
    if (!challenge?.expiresAt) return;
    const calc = () =>
      Math.max(
        0,
        Math.floor((new Date(challenge.expiresAt).getTime() - Date.now()) / 1000)
      );
    setSecondsLeft(calc());

    const timer = setInterval(() => {
      const remaining = calc();
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [challenge?.expiresAt]);

  if (!challenge || challenge.status !== "active" || secondsLeft <= 0) {
    return null;
  }

  const isOwner = String(group?.owner) === String(user?._id || user?.id);
  const isCreator = String(challenge.creator?._id || challenge.creator) === String(user?._id || user?.id);
  const canCancel = isOwner || isCreator;

  const leaderboard = challenge.leaderboard || [];
  const topLeader = leaderboard.length > 0 ? leaderboard[0] : null;
  const currentUserId = String(user?._id || user?.id || "");
  const userAttempt =
    challenge.userAttempt ||
    leaderboard.find((p) => String(p.userId) === currentUserId);
  const hasAttempted = Boolean(challenge.hasUserAttempted || userAttempt);
  const userRank = challenge.userRank || userAttempt?.rank;

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-violet-900 via-indigo-900 to-purple-950 p-5 md:p-6 text-white shadow-xl shadow-indigo-950/20 border border-indigo-500/30 animate-in fade-in duration-300">
      {/* Ambient background glow & particles */}
      <div className="absolute -right-16 -top-16 w-56 h-56 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute left-1/3 -bottom-20 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute right-12 bottom-0 text-white/[0.04] text-8xl font-black select-none pointer-events-none">
        ⚡
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        {/* Left Side: Challenge Info & Ticker */}
        <div className="space-y-3 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black bg-amber-400 text-amber-950 shadow-md shadow-amber-400/20 tracking-wide uppercase">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-600 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-900"></span>
              </span>
              <span>Active Challenge</span>
            </span>

            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-indigo-200 border border-white/10 backdrop-blur-md">
              <i className="fa-solid fa-clock text-amber-300 text-xs"></i>
              <span>Ends in:</span>
              <strong className="text-white font-mono">{formatTimeRemaining(secondsLeft)}</strong>
            </span>

            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-indigo-200 border border-white/10">
              <i className="fa-solid fa-stopwatch text-indigo-300 text-xs"></i>
              <span>{challenge.timePerQuestion || 20}s / question</span>
            </span>

            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-indigo-200 border border-white/10">
              <i className="fa-solid fa-list-ol text-indigo-300 text-xs"></i>
              <span>{challenge.questionCount || 10} Questions</span>
            </span>
          </div>

          <div>
            <h2 className="text-xl md:text-2xl font-black text-white tracking-tight flex items-center gap-2 truncate">
              <span>{challenge.title || "Group Sprint Challenge"}</span>
            </h2>
            <p className="text-sm text-indigo-200/90 font-medium flex items-center gap-1.5 mt-0.5">
              <span>Module:</span>
              <strong className="text-white font-bold">{challenge.reviewerTitle || "Shared Reviewer"}</strong>
            </p>
          </div>

          {/* Leaderboard Summary Chips */}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 px-3 py-1.5 rounded-xl text-xs backdrop-blur-md">
              <i className="fa-solid fa-users text-indigo-300 text-xs"></i>
              <span>Participants:</span>
              <strong className="text-white font-bold">{challenge.participantsCount || 0} completed</strong>
            </div>

            {topLeader ? (
              <div className="inline-flex items-center gap-2 bg-amber-400/15 border border-amber-300/30 px-3 py-1.5 rounded-xl text-xs backdrop-blur-md">
                <span className="text-base leading-none">👑</span>
                <span>Current Leader:</span>
                <strong className="text-amber-200 font-bold">
                  {topLeader.firstName} {topLeader.lastName}
                </strong>
                <span className="bg-amber-400/30 text-amber-100 font-mono font-bold px-1.5 py-0.5 rounded text-[11px]">
                  {topLeader.totalPoints} pts
                </span>
              </div>
            ) : (
              <div className="inline-flex items-center gap-1.5 text-xs text-indigo-300/80 italic">
                <i className="fa-solid fa-trophy text-amber-300/50"></i>
                <span>No scores yet. Be the first to claim #1!</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Side: Action CTAs */}
        <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end gap-2.5 shrink-0">
          {!hasAttempted ? (
            <button
              type="button"
              onClick={onAcceptChallenge}
              className="relative inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-sm font-black bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-amber-950 shadow-lg shadow-amber-500/30 hover:shadow-amber-500/40 hover:scale-[1.03] active:scale-95 transition-all group"
            >
              <i className="fa-solid fa-bolt text-amber-900 group-hover:scale-125 transition-transform"></i>
              <span>Accept Challenge</span>
              <i className="fa-solid fa-arrow-right text-xs opacity-75 group-hover:translate-x-1 transition-transform"></i>
            </button>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                <i className="fa-solid fa-circle-check text-emerald-400 text-sm"></i>
                <span>Completed: Rank #{userRank || "—"}</span>
              </span>
              <button
                type="button"
                onClick={onViewLeaderboard}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-white text-indigo-900 hover:bg-indigo-50 shadow-md transition active:scale-95"
              >
                <i className="fa-solid fa-trophy text-amber-500 text-xs"></i>
                <span>View Standings</span>
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            {!hasAttempted && (
              <button
                type="button"
                onClick={onViewLeaderboard}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-white/10 hover:bg-white/20 text-indigo-100 border border-white/15 backdrop-blur-md transition"
              >
                <i className="fa-solid fa-chart-simple text-xs"></i>
                <span>Leaderboard</span>
              </button>
            )}

            {onOpenHistory && (
              <button
                type="button"
                onClick={onOpenHistory}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-white/10 hover:bg-white/20 text-amber-200 border border-white/15 backdrop-blur-md transition"
                title="View past challenge hall of fame"
              >
                <i className="fa-solid fa-trophy text-amber-400 text-xs"></i>
                <span>Hall of Fame</span>
              </button>
            )}

            {canCancel && (
              <button
                type="button"
                onClick={onCancelChallenge}
                className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium text-rose-300 hover:text-white hover:bg-rose-500/20 transition"
                title="Cancel challenge early"
              >
                <i className="fa-solid fa-ban text-[11px]"></i>
                <span>Cancel</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveChallengeBanner;
